<template>
  <div class="flex h-auto bg-gray-900 text-white">
    <!-- Sidebar -->
    <aside class="w-64 bg-gray-800 p-6 flex flex-col space-y-6 shadow-lg bg-gradient-to-b from-gray-800 to-gray-900 border border-gray-700">
      <div class="flex items-center justify-center mb-4">
        <img src="@/assets/logo.svg" alt="Vultric Logo" class="h-20" />
      </div>
      <Navbar />
    </aside>

    <!-- Main Content -->
    <div class="flex-1 flex flex-col">
      <header class="h-16 bg-gray-800 flex items-center justify-between px-6 shadow-md bg-gradient-to-b from-gray-800 to-gray-900 border-b border-gray-700">
        <h2 class="text-xl font-semibold">Managed Databases</h2>
        <button @click="showCreateModal = true" class="bg-green-500 hover:bg-green-600 px-4 py-2 rounded transition">
          + Create Database
        </button>
      </header>

      <main class="p-6 flex-1">
        <!-- Database Table -->
        <div class="bg-gradient-to-b from-gray-800 to-gray-900 border border-gray-700 rounded-xl shadow-lg p-6 hover:shadow-xl transform hover:-translate-y-1 transition">
          <h3 class="text-lg font-semibold mb-4">Your Managed Databases</h3>
          <table class="w-full text-left border-collapse">
            <thead>
              <tr class="bg-gray-700">
                <th class="p-3">Name</th>
                <th class="p-3">Type</th>
                <th class="p-3">Status</th>
                <th class="p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="db in databases" :key="db.id" class="border-b border-gray-600 hover:bg-gray-700 transition">
                <td class="p-3 font-medium">{{ db.name }}</td>
                <td class="p-3">{{ db.type }}</td>
                <td class="p-3">
                  <span :class="db.status === 'active' ? 'bg-green-600 px-2 py-1 rounded text-sm' : 'bg-red-600 px-2 py-1 rounded text-sm'">
                    {{ db.status }}
                  </span>
                </td>
                <td class="p-3 flex space-x-2">
                  <button @click="viewDatabase(db)" class="bg-blue-500 hover:bg-blue-600 px-3 py-1 rounded transition">View</button>
                  <button @click="confirmDelete(db.id)" class="bg-red-500 hover:bg-red-600 px-3 py-1 rounded transition">Delete</button>
                </td>
              </tr>
              <tr v-if="databases.length === 0">
                <td colspan="4" class="text-center p-4 text-gray-400">No databases found</td>
              </tr>
            </tbody>
          </table>
        </div>
      </main>
    </div>

    <!-- Create Database Modal -->
    <div v-if="showCreateModal" class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
      <div class="bg-gray-800 p-6 rounded-xl shadow-lg w-96">
        <h3 class="text-lg font-semibold mb-4">Create Managed Database</h3>
        <input v-model="newDatabase.name" placeholder="Database Name" class="w-full p-2 mb-3 rounded bg-gray-700 text-white" />
        <select v-model="newDatabase.type" class="w-full p-2 mb-3 rounded bg-gray-700 text-white">
          <option value="mysql">MySQL</option>
          <option value="postgresql">PostgreSQL</option>
          <option value="mongodb">MongoDB</option>
        </select>

        <div class="bg-gray-700 p-4 rounded mb-4">
          <h4 class="font-semibold mb-2">Pricing:</h4>
          <ul class="text-sm text-gray-200">
            <li>💾 MySQL Managed: $15 / month</li>
            <li>💾 PostgreSQL Managed: $18 / month</li>
            <li>💾 MongoDB Managed: $20 / month</li>
          </ul>
        </div>

        <div class="flex justify-end space-x-2">
          <button @click="showCreateModal = false" class="bg-gray-600 hover:bg-gray-700 px-4 py-2 rounded transition">Cancel</button>
          <button @click="createDatabase" class="bg-green-500 hover:bg-green-600 px-4 py-2 rounded transition">Create</button>
        </div>
      </div>
    </div>

    <!-- View Database Modal -->
    <div v-if="selectedDatabase" class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
      <div class="bg-gray-800 p-6 rounded-xl shadow-lg w-96">
        <h3 class="text-lg font-semibold mb-4">Database Details</h3>
        <div class="space-y-2">
          <p><strong>Name:</strong> {{ selectedDatabase.name }}</p>
          <p><strong>Type:</strong> {{ selectedDatabase.type }}</p>
          <p><strong>DB User:</strong> {{ selectedDatabase.username }}</p>
          <p><strong>DB Password:</strong> <span class="bg-gray-700 px-2 py-1 rounded">{{ selectedDatabase.password }}</span></p>
          <p><strong>Host:</strong> {{ selectedDatabase.host }}</p>
          <p><strong>Port:</strong> {{ selectedDatabase.port }}</p>
          <p><strong>Status:</strong>
            <span :class="selectedDatabase.status === 'active' ? 'bg-green-600 px-2 py-1 rounded text-sm' : 'bg-red-600 px-2 py-1 rounded text-sm'">
              {{ selectedDatabase.status }}
            </span>
          </p>
          <p v-if="selectedDatabase.type === 'mysql' && selectedDatabase.phpmyadmin"><strong>phpMyAdmin:</strong>
            <a :href="selectedDatabase.phpmyadmin" target="_blank" class="text-blue-400 underline">Open</a>
          </p>
        </div>

        <div class="flex justify-end mt-4">
          <button @click="selectedDatabase = null" class="bg-gray-600 hover:bg-gray-700 px-4 py-2 rounded transition">Close</button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="deleteDatabaseId" class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
      <div class="bg-gray-800 p-6 rounded-xl shadow-lg w-96">
        <h3 class="text-lg font-semibold mb-4 text-red-500">Confirm Delete</h3>
        <p class="mb-4">Are you sure you want to delete this database? This action cannot be undone.</p>
        <div class="flex justify-end space-x-2">
          <button @click="deleteDatabaseId = null" class="bg-gray-600 hover:bg-gray-700 px-4 py-2 rounded transition">Cancel</button>
          <button @click="deleteDatabase(deleteDatabaseId)" class="bg-red-500 hover:bg-red-600 px-4 py-2 rounded transition">Delete</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Navbar from '@/components/nav.vue';
import axios from 'axios';

export default {
  name: "ManagedDatabases",
  components: { Navbar },
  data() {
    return {
      databases: [],
      showCreateModal: false,
      newDatabase: { name: "", type: "mysql" },
      selectedDatabase: null,
      deleteDatabaseId: null
    };
  },
  methods: {
    async fetchDatabases() {
      try {
        const res = await axios.get('https://api.syniac.com:3000/api/database/get', { withCredentials: true });
        this.databases = res.data;
      } catch (error) {
        console.error("Error fetching databases:", error);
      }
    },
    async createDatabase() {
      if (!this.newDatabase.name) return;
      try {
        await axios.post('https://api.syniac.com:3000/api/database/add', this.newDatabase, { withCredentials: true });
        this.newDatabase.name = "";
        this.showCreateModal = false;
        this.fetchDatabases();
      } catch (error) {
        console.error("Error creating database:", error);
      }
    },
    confirmDelete(id) {
      this.deleteDatabaseId = id;
    },
    async deleteDatabase(id) {
      try {
        await axios.delete(`https://api.syniac.com:3000/api/database/${id}`, { withCredentials: true });
        this.databases = this.databases.filter(db => db.id !== id);
        this.deleteDatabaseId = null;
      } catch (error) {
        console.error("Error deleting database:", error);
      }
    },
    viewDatabase(db) {
      this.selectedDatabase = {
        ...db,
        username: db.username || "admin",
        password: db.password || "********",
        host: db.host || "db.syniac.com",
        port: db.port || (db.type === "mysql" ? 3306 : db.type === "postgresql" ? 5432 : 27017),
        phpmyadmin: db.type === "mysql" ? "https://phpmyadmin.example.com" : null
      };
    }
  },
  mounted() {
    this.fetchDatabases();
  }
};
</script>
